import { useTranslation } from 'react-i18next';
import { ShoppingCart, Truck } from 'lucide-react';
import { useCan } from '../../../lib/rbac';

/** Orden de Amazon ligada a lo escaneado (campo `amazon_orders` del escaneo, 2026-10-17). */
export interface ScanAmazonOrder {
  amazon_order_id: string;
  ml_order_id: string | null;
  asin: string | null;
  amazon_status: string | null;
}

export const amazonOrderUrl = (amazonOrderId: string) =>
  `https://www.amazon.com/gp/your-account/order-details?orderID=${encodeURIComponent(amazonOrderId)}`;

/**
 * Franja con el número de orden de Amazon de la venta escaneada y, al lado, el mismo botón
 * "Solicitar devolución en Amazon" de las canceladas/devoluciones: abre la compra en la web de
 * Amazon (la API no permite pedir la devolución). El número se ve siempre; el botón pide la
 * llave `warehouse.request_amazon_cancel`, igual que en las canceladas.
 */
export function AmazonOrdersStrip({
  orders,
  showReturn = true,
}: {
  orders: ScanAmazonOrder[] | undefined | null;
  /** `false` cuando el pie del pop-up ya ofrece el botón (venta cancelada). */
  showReturn?: boolean;
}) {
  const { t } = useTranslation();
  const can = useCan();
  if (!orders || orders.length === 0) return null;
  const canReturn = showReturn && can('warehouse.request_amazon_cancel');
  return (
    <div className="shrink-0 mx-4 mt-2 flex flex-col gap-1.5">
      {orders.map((o) => (
        <div
          key={o.amazon_order_id}
          className="flex items-center gap-2 flex-wrap px-3 py-1.5 rounded-xl border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20"
        >
          <ShoppingCart size={15} className="shrink-0 text-blue-600 dark:text-blue-400" />
          <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
            {t('wh.amazonOrder')}
          </span>
          <span className="font-mono text-sm font-bold text-[var(--color-text)] select-all">{o.amazon_order_id}</span>
          {orders.length > 1 && o.asin && (
            <span className="font-mono text-[11px] text-[var(--color-text-secondary)]">· {o.asin}</span>
          )}
          {canReturn && (
            <a
              href={amazonOrderUrl(o.amazon_order_id)}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold"
              title={t('wh.return.amazonReturnHint')}
            >
              <Truck size={14} />
              {t('wh.return.requestAmazonReturn')}
            </a>
          )}
        </div>
      ))}
    </div>
  );
}
